import { path } from 'ramda'

import { isAbortError } from '@common/utils'

const LIVE_METRICS_DATA_LOADING = 'LIVE_METRICS_DATA_LOADING'
const LIVE_METRICS_DATA_LOADED = 'LIVE_METRICS_DATA_LOADED'
const LIVE_METRICS_DATA_FAILED = 'LIVE_METRICS_DATA_FAILED'

const LIVE_METRICS_RESET_STATE = 'LIVE_METRICS_RESET_STATE'

const defaultState = {}

const entityName = 'liveMetrics'

export default {
  name: entityName,
  reducer: (state, action) => {
    if (action.type === LIVE_METRICS_RESET_STATE) {
      return defaultState
    }
    if (action.type === LIVE_METRICS_DATA_LOADING) {
      return { ...state, data: { ...action.meta, payload: action.payload } }
    }
    if (action.type === LIVE_METRICS_DATA_LOADED) {
      return { ...state, data: { ...action.meta, data: action.payload } }
    }
    if (action.type === LIVE_METRICS_DATA_FAILED) {
      return { ...state, data: { ...action.meta, error: action.payload } }
    }
    return state || defaultState
  },
  selectLiveMetricsDataIsLoading: ({ liveMetrics }) => {
    const status = path(['data', 'status'], liveMetrics)
    return status === 'loading'
  },
  selectLiveMetricsData: ({ liveMetrics }) => path(['data', 'data'], liveMetrics),
  selectLiveMetricsDataError: ({ liveMetrics }) => path(['data', 'error'], liveMetrics),
  doResetLiveMetricsState:
    () =>
    ({ dispatch }) =>
      dispatch({ type: LIVE_METRICS_RESET_STATE }),
  doFetchLiveMetricsData:
    () =>
    async ({ dispatch, apiFetch }) => {
      try {
        dispatch({
          type: LIVE_METRICS_DATA_LOADING,
          payload: null,
          meta: { status: 'loading' },
        })

        const propertiesPayload = {
          active: true,
          pageSize: 1,
          status: 'LIVE',
        }
        const unitsPayload = {
          active: true,
          pageSize: 1,
          propertyStatus: 'LIVE',
          status: ['ONLINE', 'OFFLINE'].join(','),
        }

        const apiResults = await Promise.all([
          apiFetch('/properties/', propertiesPayload, {
            cancelationPrefix: `${entityName}_count`,
          }),
          apiFetch('/units/', unitsPayload, {
            cancelationPrefix: `${entityName}_count`,
          }),
          apiFetch(
            '/organization_groups/',
            { pageSize: 999999 },
            { cancelationPrefix: entityName },
          ),
        ])

        const totalProperties = apiResults[0]?.count ?? 0
        const totalUnits = apiResults[1]?.count ?? 0
        const orgGroupsResponse = apiResults[2]

        const summaryResponse = await Promise.all([
          Promise.all(
            orgGroupsResponse?.results?.map(({ id }) =>
              apiFetch(
                '/properties/',
                {
                  ...propertiesPayload,
                  organizationGroup: id,
                },
                { cancelationPrefix: `${entityName}_properties_org_group_${id}` },
              ),
            ),
          ),
          Promise.all(
            orgGroupsResponse?.results?.map(({ id }) =>
              apiFetch(
                '/units/',
                {
                  ...unitsPayload,
                  organizationGroup: id,
                },
                { cancelationPrefix: `${entityName}_units_org_group_${id}` },
              ),
            ),
          ),
        ])

        const propertiesOrgGroupSummary = summaryResponse[0]
        const unitsOrgGroupSummary = summaryResponse[1]

        const calculatePercentage = (part, total) =>
          Number(((part / total) * 100).toFixed(1))

        const processedSummaries = orgGroupsResponse?.results?.reduce(
          (acc, { id, name }, index) => {
            const updated = { ...acc }
            const propertiesCount = propertiesOrgGroupSummary?.[index]?.count ?? 0
            const unitsCount = unitsOrgGroupSummary?.[index]?.count ?? 0
            if (propertiesCount) {
              updated.properties.push({
                id,
                name,
                count: propertiesCount,
                percentage: calculatePercentage(propertiesCount, totalProperties),
              })
            }
            if (unitsCount) {
              updated.units.push({
                id,
                name,
                count: unitsCount,
                percentage: calculatePercentage(unitsCount, totalUnits),
              })
            }
            if (index === orgGroupsResponse.count - 1) {
              const totalGroupedProperties = updated.properties.reduce(
                (totalPropAcc, { count }) => totalPropAcc + count,
                0,
              )
              const totalGroupedUnits = updated.units.reduce(
                (totalUnitsAcc, { count }) => totalUnitsAcc + count,
                0,
              )
              const propertiesNoGroupCount = totalProperties - totalGroupedProperties
              const unitsNoGroupCount = totalUnits - totalGroupedUnits
              if (propertiesNoGroupCount > 0) {
                updated.properties.push({
                  id: null,
                  name: 'No Group',
                  count: propertiesNoGroupCount,
                  percentage: calculatePercentage(
                    propertiesNoGroupCount,
                    totalProperties,
                  ),
                })
              }
              if (unitsNoGroupCount > 0) {
                updated.units.push({
                  id: null,
                  name: 'No Group',
                  count: unitsNoGroupCount,
                  percentage: calculatePercentage(unitsNoGroupCount, totalUnits),
                })
              }
            }
            return updated
          },
          { properties: [], units: [] },
        )

        const result = {
          properties: {
            total: totalProperties,
            summary: processedSummaries.properties,
          },
          units: {
            total: totalUnits,
            summary: processedSummaries.units,
          },
        }

        dispatch({
          type: LIVE_METRICS_DATA_LOADED,
          payload: result,
          meta: { status: 'succeeded' },
        })
        return result
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: LIVE_METRICS_DATA_FAILED,
            payload: err,
            meta: { status: 'failed' },
          })
          throw err
        }
        return null
      }
    },
}
